import { getInitials } from '@tempo-io/string-util';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { AccountCircle, Close } from '@tempo-io/sui-icons';
import { useTranslation } from 'react-i18next';
import { useOrgMemberByIdQuery } from '@/features/settings/user-management/queries/useOrgMemberByIdQuery';

interface UserDisplayButtonProps {
  userId?: string;
  onClick?: () => void;
  onRemove?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
}

export const UserDisplayButton = ({
  userId,
  onClick,
  onRemove,
  isDisabled = false,
  placeholder,
}: UserDisplayButtonProps) => {
  const { t } = useTranslation();
  const { data: user } = useOrgMemberByIdQuery(userId || '');

  const defaultPlaceholder =
    placeholder || t(($) => $.adaptivePlanner.itemPanel.userField.selectUser);
  const hasUser = !!user;
  const isClickable = !isDisabled && !!onClick;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        disabled={!isClickable}
        className={cn(
          'border-border-neutral bg-bg-surface-base flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors w-full',
          isClickable && 'hover:bg-bg-neutral-subtle cursor-pointer',
          !isClickable && 'cursor-default',
          hasUser && onRemove && !isDisabled && 'pr-10',
        )}
      >
        {hasUser ? (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Avatar size="sm">
              <AvatarImage src={user.pictureUrl} alt={user.name || user.email} />
              <AvatarFallback className="text-xs">
                {getInitials(user.name || user.email)}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{user.name || user.email}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-text-neutral-secondary">
            <AccountCircle size={16} />
            <span>{defaultPlaceholder}</span>
          </div>
        )}
      </button>
      {hasUser && onRemove && !isDisabled && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-1/2 -translate-y-1/2 size-6 p-0 hover:bg-bg-surface-base z-10"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t(($) => $.adaptivePlanner.itemPanel.userField.clear)}
        >
          <Close size={16} />
        </Button>
      )}
    </div>
  );
};
